import { Injectable } from '@angular/core';
import { API_ENDPOINT, CACHE_LIFESPAN } from '@core/configs';
import { firstValueFrom } from 'rxjs';
import { DeleteSupportStaffDTO, EditSupportStaffDTO, InviteSupportStaffDTO } from '@core/models/dto/user-role.dto';
import { UserPaginateDTO } from '@core/models/dto/paginate.dto';
import { CareconneUserPaginateRO } from '@core/models/interfaces/user-role.ro';
import { ApiResponse } from '@core/models/interfaces';
import { ApiService } from '../common/api.service';
import { CacheService } from '../common/cache.service';

const CONFIG_CALL_API_MULTI_TIMES = {
  ORGS_NAME: {
    LIMIT: 100,
    MAX_CALLS: 1000, // unlimited calls, but safety guard to avoid infinite loop if API misbehaves
  },
};

@Injectable({
  providedIn: 'root',
})
export class UserRoleService {
  constructor(
    private readonly http: ApiService,
    private readonly cacheService: CacheService,
  ) {}

  getPaginate(query: UserPaginateDTO) {
    return firstValueFrom(
      this.http.getWithQuery<CareconneUserPaginateRO>(
        API_ENDPOINT.userRole.controller + API_ENDPOINT.userRole.paginate,
        query as any,
      ),
    );
  }

  getDetails(userId: number) {
    return firstValueFrom(
      this.http.get<any>(API_ENDPOINT.userRole.controller + API_ENDPOINT.userRole.getDetails + userId),
    );
  }

  getUserIdByEmail(email) {
    return this.http.post(
      API_ENDPOINT.userRole.controller + API_ENDPOINT.userRole.getUserIdByEmail,
      email,
    );
  }

  getSupportStaff(companyId) {
    return this.http.get(
      API_ENDPOINT.userRole.controller + API_ENDPOINT.userRole.getSupportStaff + companyId,
    );
  }

  inviteSupportStaff(newSupportStaff: InviteSupportStaffDTO) {
    return this.http.post(
      API_ENDPOINT.userRole.controller + API_ENDPOINT.userRole.inviteSupportStaff,
      newSupportStaff,
    );
  }

  editSupportStaff(editSupportStaff: EditSupportStaffDTO) {
    return this.http.put(
      API_ENDPOINT.userRole.controller + API_ENDPOINT.userRole.editSupportStaff,
      editSupportStaff,
    );
  }

  deleteSupportStaff(deleteSupportStaff: DeleteSupportStaffDTO) {
    return this.http.put(
      API_ENDPOINT.userRole.controller + API_ENDPOINT.userRole.deleteSupportStaff,
      deleteSupportStaff,
    );
  }

  deleteAccount(userId: number) {
    return this.http.delete(
      API_ENDPOINT.userRole.controller + API_ENDPOINT.userRole.deleteAccount + userId
    )
  }

  async getOrganizationsName() {
    const limit = CONFIG_CALL_API_MULTI_TIMES.ORGS_NAME.LIMIT;
    const maxCalls = CONFIG_CALL_API_MULTI_TIMES.ORGS_NAME.MAX_CALLS;
    const aggregated: any[] = [];

    for (let callIndex = 0; callIndex < maxCalls; callIndex++) {
      const offset = callIndex * limit;

      // Call API and wait for result
      const response = await this.getOrganizationsNameWithCache({ offset, limit });
      const data = response?.result?.data;

      if (!Array.isArray(data)) {
        throw new Error('ERR: API to return an array for paginated endpoint');
      }

      if (data?.length) {
        aggregated.push(...data);
      } else {
        break;
      }

      if (!response?.result?.hasMore) {
        break;
      }

      // If reach maxCalls then loop stop
    }

    return aggregated;
  }

  private async getOrganizationsNameWithCache(query: { offset: number; limit: number }): Promise<ApiResponse<any>> {
    const { offset, limit } = query;
    const url = `${API_ENDPOINT.userRole.organizationsName}?offset=${offset}&limit=${limit}`;
    const cacheRequest = this.cacheService.createCacheRequest(url, { method: 'GET' });
    const cache = await this.cacheService.open();
    const cachedResponse = await this.cacheService.getCachedResponse(cache, cacheRequest, CACHE_LIFESPAN.orgsName);
    if (cachedResponse) {
      return cachedResponse as ApiResponse<any>;
    }

    // Call API and wait for result
    const res = await firstValueFrom(
      this.http.getWithQuery(API_ENDPOINT.userRole.controller + API_ENDPOINT.userRole.organizationsName, {
        offset,
        limit,
      }),
    ).catch(e => {
      console.error(e); // NOSONAR
      throw new Error('ERR: API organizationsName');
    });
    if (res?.result) {
      await this.cacheService.put(cache, cacheRequest, res);
    }
    return res;
  }
}
